import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { useT } from "@/lib/i18n";
import { downloadBlob } from "@/lib/resumeUtils";
import WizardModal from "@/components/resume/WizardModal";
import ThinkingLoader from "@/components/ThinkingLoader";
import { Sparkles, Send, RefreshCw, FileDown, Video } from "lucide-react";
import { toast } from "sonner";

export default function EmailWizard({ job, onClose, onFollowedUp }) {
  const { t, lang } = useT();
  const staleDays = (() => {
    if (job.status !== "applied" || !job.date_applied) return 0;
    const d = new Date(job.date_applied);
    return isNaN(d) ? 0 : Math.floor((Date.now() - d.getTime()) / 86400000);
  })();
  const [emailType, setEmailType] = useState(staleDays >= 7 ? "followup" : "intro");
  const [to, setTo] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [generating, setGenerating] = useState(true);
  const [dossier, setDossier] = useState(null);
  const [defaultResumeId, setDefaultResumeId] = useState(null);
  const [attach, setAttach] = useState({ cv: true, letter: false, package: false });
  // Pitch link auto-attach — pulls the best matching video pitch (letter
  // → favorite → elevator) and appends its public URL to the email body
  // on send. Loaded once per open so the toggle can enable/disable it
  // without another network hit.
  const [pitchAttach, setPitchAttach] = useState(false);
  const [pitchLink, setPitchLink] = useState(null); // { url, source } | null
  const [busy, setBusy] = useState(false);

  const generate = useCallback(async (type) => {
    setGenerating(true);
    try {
      const { data } = await api.post(`/jobs/${job.id}/intro-email`, { lang, email_type: type });
      setSubject(data.subject);
      setBody(data.body);
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("emwGenerateFailed"));
    } finally {
      setGenerating(false);
    }
  }, [job.id, lang, t]);

  useEffect(() => {
    generate(emailType);
    api.get(`/letters/by-job/${job.id}`)
      .then(({ data }) => {
        if (data.present) {
          setDossier(data);
          setAttach((a) => ({ ...a, letter: true }));
        }
      })
      .catch(() => {});
    api.get("/resumes")
      .then(({ data }) => {
        const def = data.find((v) => v.is_default) || data[0];
        setDefaultResumeId(def?.id || null);
      })
      .catch(() => {});
    // Best matching pitch: (1) tied to the letter, (2) tied to the
    // saved favorite for this job, (3) generic elevator pitch. First
    // hit wins so the toggle shows the most specific URL possible.
    (async () => {
      const origin = window.location.origin;
      // 1. letter-linked
      try {
        const { data: letterDossier } = await api.get(`/letters/by-job/${job.id}`);
        if (letterDossier?.letter?.id) {
          const r = await api.get(`/video-letters/by-letter/${letterDossier.letter.id}`);
          const v = (r.data || [])[0];
          if (v?.public_token) {
            setPitchAttach(true);
            setPitchLink({ url: `${origin}/video/${v.public_token}`, source: "letter" });
            return;
          }
        }
      } catch { /* fall through */ }
      // 2. favorite-linked
      try {
        const favResp = await api.get("/jobs-search/favorites");
        const favs = favResp?.data?.favorites || [];
        const fav = favs.find(
          (f) => f.external_id === job.external_id || f.external_id === job.id || f.job_id === job.id || f.id === job.id,
        );
        // Favorites are keyed by `external_id` server-side; fall back to
        // the legacy `id` field just in case older docs still expose it.
        const favKey = fav?.external_id || fav?.id;
        if (favKey) {
          const r = await api.get(`/video-letters/by-favorite/${favKey}`);
          const v = (r.data || [])[0];
          if (v?.public_token) {
            setPitchAttach(true);
            setPitchLink({ url: `${origin}/video/${v.public_token}`, source: "favorite" });
            return;
          }
        }
      } catch { /* fall through */ }
      // 3. elevator pitch
      try {
        const { data } = await api.get("/video-letters/elevator-pitch");
        if (data?.public_token) {
          setPitchAttach(false); // opt-in for generic pitch — user must switch it on
          setPitchLink({ url: `${origin}/video/${data.public_token}`, source: "elevator" });
        }
      } catch { /* leave null */ }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [job.id]);

  const switchType = (type) => {
    if (type === emailType || generating) return;
    setEmailType(type);
    generate(type);
  };

  const letter = dossier?.letter;
  const cvResumeId = (dossier?.resume_exists && letter?.resume_id) || defaultResumeId;
  const cvLang = letter?.lang || lang;

  const openMail = async () => {
    setBusy(true);
    try {
      const safe = (job.company || "job").replace(/[^A-Za-z0-9_-]+/g, "_");
      let downloaded = false;
      if (attach.cv && cvResumeId) {
        // letter?.id → auto-align CV title with vacancy on this render.
        const letterHint = letter?.id ? `&letter_id=${letter.id}` : "";
        const res = await api.get(`/resumes/${cvResumeId}/pdf?lang=${cvLang}${letterHint}`, { responseType: "blob" });
        downloadBlob(new Blob([res.data], { type: "application/pdf" }), `cv_${safe}_${cvLang}.pdf`);
        downloaded = true;
      }
      if (attach.letter && letter) {
        const res = await api.get(`/letters/${letter.id}/pdf?doc=letter`, { responseType: "blob" });
        const prefix = letter.lang === "nl" ? "motivatiebrief" : "motivation_letter";
        downloadBlob(new Blob([res.data], { type: "application/pdf" }), `${prefix}_${safe}.pdf`);
        downloaded = true;
      }
      if (attach.package && letter) {
        const res = await api.get(`/letters/${letter.id}/package`, { responseType: "blob" });
        const prefix = letter.lang === "nl" ? "sollicitatiepakket" : "application_package";
        downloadBlob(new Blob([res.data], { type: "application/pdf" }), `${prefix}_${safe}.pdf`);
        downloaded = true;
      }
      // Record the follow-up BEFORE opening the mail client (mailto may navigate)
      if (emailType === "followup") {
        try {
          await api.post(`/jobs/${job.id}/followed-up`);
          onFollowedUp?.();
          toast.success(t("emwFollowedUpMarked"));
        } catch { /* non-blocking */ }
      }
      // Compose the final body with an optional pitch-link footer so
      // recruiters see the video link right in the plaintext mail
      // client (mailto: bodies stay text-only).
      let finalBody = body;
      if (pitchAttach && pitchLink?.url) {
        const label = t("emwPitchAttachIntro");
        finalBody = `${body.replace(/\s+$/, "")}\n\n${label}\n${pitchLink.url}`;
      }
      const mailto = `mailto:${encodeURIComponent(to.trim())}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(finalBody)}`;
      const a = document.createElement("a");
      a.href = mailto;
      a.click();
      if (downloaded) toast.info(t("emwAttachHint"), { duration: 8000 });
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("rbPdfFailed"));
    } finally {
      setBusy(false);
    }
  };

  const attachOptions = [
    { key: "cv", label: t("emwAttachCv"), enabled: !!cvResumeId },
    { key: "letter", label: t("emwAttachLetter"), enabled: !!letter },
    { key: "package", label: t("emwAttachPackage"), enabled: !!letter },
  ];

  return (
    <WizardModal testId="email-wizard" title={t("emwTitle")} onClose={onClose} wide>
      <p className="font-serif text-xl italic text-neutral-800 mb-1">{job.job_title}</p>
      <p className="font-mono text-xs text-neutral-500 mb-5">{job.company}</p>

      <div className="space-y-5">
        <div>
          <p className="ed-label mb-2">{t("emwType")}</p>
          <div className="flex gap-2">
            {[
              { key: "intro", label: t("emwTypeIntro") },
              { key: "followup", label: t("emwTypeFollowup") },
            ].map(({ key, label }) => (
              <button
                key={key}
                data-testid={`email-wizard-type-${key}`}
                onClick={() => switchType(key)}
                disabled={generating}
                className={`font-mono text-[0.65rem] uppercase tracking-[0.15em] px-4 py-2 border transition-colors disabled:opacity-50 ${
                  emailType === key
                    ? "border-neutral-900 bg-neutral-900 text-[#F9F8F6]"
                    : "border-neutral-300 hover:border-neutral-900"
                }`}
              >
                {label}
              </button>
            ))}
          </div>
          {staleDays >= 7 && (
            <p className="text-[0.65rem] font-mono text-amber-700 mt-2" data-testid="email-wizard-stale-hint">
              {t("emwStaleHint", { days: staleDays })}
            </p>
          )}
        </div>

        <div>
          <label className="ed-label">{t("emwTo")}</label>
          <input
            data-testid="email-wizard-to"
            type="email"
            className="ed-input max-w-md"
            placeholder={t("emwToPh")}
            value={to}
            onChange={(e) => setTo(e.target.value)}
          />
        </div>

        <div>
          <p className="ed-label mb-2">{t("emwAttach")}</p>
          <div className="flex flex-wrap gap-2">
            {attachOptions.map(({ key, label, enabled }) => (
              <button
                key={key}
                data-testid={`email-wizard-attach-${key}`}
                disabled={!enabled}
                onClick={() => setAttach((a) => ({ ...a, [key]: !a[key] }))}
                className={`font-mono text-[0.65rem] uppercase tracking-[0.15em] px-3 py-1.5 border transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
                  attach[key] && enabled
                    ? "border-neutral-900 bg-neutral-900 text-[#F9F8F6]"
                    : "border-neutral-300 hover:border-neutral-900"
                }`}
              >
                <FileDown size={11} strokeWidth={1.5} className="inline mr-1.5 -mt-0.5" />
                {label}
              </button>
            ))}
          </div>
          {!letter && (
            <p className="text-[0.65rem] font-mono text-neutral-500 mt-2" data-testid="email-wizard-no-letter">{t("emwNoLetter")}</p>
          )}
          {/* Video-pitch link auto-attach — toggle appears when the user
              has any pitch (letter-linked, favorite-linked, or elevator).
              When enabled, the public URL is appended to the mail body
              on send so recruiters see the video without extra steps. */}
          {pitchLink?.url && (
            <div
              className="mt-3 border border-amber-300 bg-amber-50/60 p-3 flex items-start gap-3"
              data-testid="email-wizard-pitch-attach"
            >
              <Video size={13} strokeWidth={1.5} className="text-amber-700 mt-0.5 shrink-0" />
              <div className="flex-1 min-w-0">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    data-testid="email-wizard-pitch-toggle"
                    checked={pitchAttach}
                    onChange={(e) => setPitchAttach(e.target.checked)}
                    className="w-3.5 h-3.5"
                  />
                  <span className="font-mono text-[0.65rem] uppercase tracking-[0.15em] text-amber-900">
                    {t("emwPitchAttach")}
                  </span>
                  <span className="font-mono text-[0.55rem] uppercase tracking-[0.12em] text-amber-700 border border-amber-500 px-1.5 py-0.5">
                    {pitchLink.source === "letter"
                      ? t("emwPitchSrcLetter")
                      : pitchLink.source === "favorite"
                        ? t("emwPitchSrcFavorite")
                        : t("emwPitchSrcElevator")}
                  </span>
                </label>
                <a
                  href={pitchLink.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block mt-1 truncate font-mono text-xs text-amber-800 hover:underline"
                  data-testid="email-wizard-pitch-url"
                >
                  {pitchLink.url}
                </a>
              </div>
            </div>
          )}
        </div>

        <div>
          <div className="flex items-center justify-between gap-3 mb-1">
            <label className="ed-label mb-0">{t("emwSubject")}</label>
            <button
              data-testid="email-wizard-regenerate"
              className="ed-btn ed-btn-outline"
              onClick={() => generate(emailType)}
              disabled={generating}
            >
              <RefreshCw size={12} strokeWidth={1.5} className={generating ? "animate-spin" : ""} /> {t("emwRegenerate")}
            </button>
          </div>
          <input
            data-testid="email-wizard-subject"
            className="ed-input"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            disabled={generating}
          />
        </div>

        <div>
          <label className="ed-label">{t("emwBody")}</label>
          {generating ? (
            <div className="py-6" data-testid="email-wizard-generating">
              <ThinkingLoader stage="email" testId="email-wizard-thinking" />
            </div>
          ) : (
            <textarea
              data-testid="email-wizard-body"
              className="ed-input min-h-[14rem] font-serif text-base leading-relaxed"
              value={body}
              onChange={(e) => setBody(e.target.value)}
            />
          )}
        </div>
      </div>

      <div className="flex items-center justify-end mt-6 pt-4 border-t border-neutral-200">
        <button
          data-testid="email-wizard-send"
          className="ed-btn"
          onClick={openMail}
          disabled={generating || busy || !body.trim()}
        >
          <Send size={14} strokeWidth={1.5} /> {busy ? t("ltDlBusy") : t("emwOpenMail")}
        </button>
      </div>
    </WizardModal>
  );
}
